import React, { useEffect, useRef, useState } from "react";
import { streamSSE, http } from "../lib/api";
import MarkdownLite from "../components/MarkdownLite";
import { PaperPlaneRight, ChatCircleText, Headset } from "@phosphor-icons/react";

export default function AgentChat({ agent = "hr", onDeepLink }) {
  const [sessionId] = useState(() => `${agent}-${Math.random().toString(36).slice(2, 10)}`);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [requisitionMap, setRequisitionMap] = useState({});
  const [candidateMap, setCandidateMap] = useState({});
  const scrollerRef = useRef(null);

  const title = agent === "hr" ? "HR Recruitment Agent" : "Applicant Support";
  const subtitle =
    agent === "hr"
      ? "Conversational agent for recruiters."
      : "For job applicants – process, status, and document help.";
  const placeholder =
    agent === "hr"
      ? "Ask about candidates, requisitions, or compliance…"
      : "Ask about your application, documents, or interview…";

  useEffect(() => {
    http
      .get("/ai/chat/history", { params: { session_id: sessionId, agent } })
      .then((r) => setMessages(r.data))
      .catch(() => {});
  }, [sessionId, agent]);

  // Preload local requisitions for HR-agent pill hover previews.
  useEffect(() => {
    if (agent !== "hr") return;
    http
      .get("/requisitions")
      .then((r) => {
        const map = {};
        (r.data?.drafts || []).forEach((d) => { if (d.id) map[d.id] = d; });
        setRequisitionMap(map);
      })
      .catch(() => setRequisitionMap({}));
  }, [agent, messages.length]);

  // Preload local candidates for HR-agent pill hover previews.
  useEffect(() => {
    if (agent !== "hr") return;
    http
      .get("/candidates")
      .then((r) => {
        const map = {};
        (r.data || []).forEach((c) => { if (c.id !== undefined && c.id !== null) map[String(c.id)] = c; });
        setCandidateMap(map);
      })
      .catch(() => setCandidateMap({}));
  }, [agent, messages.length]);

  useEffect(() => {
    scrollerRef.current?.scrollTo({ top: scrollerRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, streaming]);

  const send = async () => {
    if (!input.trim() || streaming) return;
    const userMsg = { role: "user", content: input };
    setMessages((m) => [...m, userMsg, { role: "assistant", content: "" }]);
    const sendText = input;
    setInput("");
    setStreaming(true);
    await streamSSE(
      "/ai/chat",
      { session_id: sessionId, message: sendText, agent },
      {
        onDelta: (_d, full) => {
          setMessages((m) => {
            const copy = [...m];
            copy[copy.length - 1] = { role: "assistant", content: full };
            return copy;
          });
        },
        onDone: () => setStreaming(false),
        onError: () => setStreaming(false),
      }
    );
  };

  const suggested =
    agent === "hr"
      ? [
          "How many open requisitions do I have right now?",
          "List my recent candidates and their pipeline status",
          "Summarize top-scoring candidates for my newest open requisition",
        ]
      : [
          "What documents do I need to submit?",
          "When will I hear back after my interview?",
          "How do I update my contact details?",
        ];

  return (
    <div data-testid={`chat-${agent}`} className="card h-[calc(100vh-6rem)] flex flex-col overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div
            className="w-9 h-9 rounded-sm grid place-items-center text-white"
            style={{ background: agent === "hr" ? "#002FA7" : "#0F766E" }}
          >
            {agent === "hr" ? <ChatCircleText size={18} weight="fill" /> : <Headset size={18} weight="fill" />}
          </div>
          <div>
            <div className="font-display font-bold tracking-tight">{title}</div>
            <div className="text-xs text-slate-500">{subtitle}</div>
          </div>
        </div>
        <div className="text-[10px] font-mono text-slate-400">claude-sonnet-4.5</div>
      </div>

      {/* Messages */}
      <div ref={scrollerRef} className="flex-1 overflow-y-auto px-6 py-6 space-y-3 bg-[#fafbfd]">
        {messages.length === 0 && (
          <div className="max-w-2xl mx-auto mt-10 text-center">
            <div className="font-display text-2xl font-bold mb-2">How can I help today?</div>
            <p className="text-slate-500 text-sm mb-5">
              {agent === "hr"
                ? "Pick a quick prompt or ask your own question."
                : "Ask anything about your application — I'll keep it brief and accurate."}
            </p>
            <div className="flex flex-wrap justify-center gap-2">
              {suggested.map((s) => (
                <button
                  key={s}
                  data-testid={`suggest-${s.slice(0, 10)}`}
                  className="btn btn-outline !py-1.5 !px-3 !text-xs"
                  onClick={() => setInput(s)}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`bubble ${m.role === "user" ? "bubble-user" : "bubble-agent"}`}>
              {m.role === "assistant" ? (
                <>
                  <MarkdownLite
                    text={m.content}
                    onDeepLink={onDeepLink}
                    requisitionMap={requisitionMap}
                    candidateMap={candidateMap}
                    onReqUpdated={(updated) =>
                      setRequisitionMap((prev) => ({ ...prev, [updated.id]: updated }))
                    }
                  />
                  {streaming && i === messages.length - 1 && <span className="cursor-blink" />}
                </>
              ) : (
                m.content
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Composer */}
      <div className="px-5 py-4 border-t bg-white relative z-20">
        <div className="flex items-end gap-2">
          <textarea
            data-testid={`chat-input-${agent}`}
            rows={1}
            className="input"
            style={{ minHeight: 44, maxHeight: 160 }}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            placeholder={placeholder}
          />
          <button
            type="button"
            data-testid={`chat-send-${agent}`}
            className="btn btn-primary relative z-30"
            onClick={send}
            disabled={streaming || !input.trim()}
          >
            <PaperPlaneRight size={16} weight="fill" />
            {streaming ? "Streaming…" : "Send"}
          </button>
        </div>
      </div>
    </div>
  );
}
